/* eslint-disable */
import { default as stripSlash } from "@jmc/utils/utils/strip-slash";
import React, { cloneElement, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import parse from "html-react-parser";
import cleanCSSIdentifier from "@jmc/utils/utils/clean-css-identifier";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { Spinner } from "@jmc/solid-design-system/src/components/atoms/Spinner/Spinner";
import urlHelper from "@jmc/solid-design-system/src/utils/url-helper";
import style from "./form-assembly-legacy.module.scss";
import { ToolTracking } from "@jmc/core/src/components/Tracking";
import { EventTypes } from "@jmc/core/src/types/EventTypes";
import analyticstracker from "@jmc/analyticstracker";
import { useTranslation } from "react-i18next";

interface Props {
    formAssemblyRestUrl: string;
    proxyUrl?: string;
    formAssemblyDomain?: string;
    compliance?: boolean;
    complianceText?: string;
    params?: object;
    title: string;
}

const SCRIPT_STATUS = {
    LOADED: 1,
    NOT_LOADED: 0,
};

// 35MB
const MAX_AGGREGATE_UPLOAD_SIZE = 36700160;

const MAX_TRIES_FILE_UPLOAD = 5;

/**
 * Fore more details on FormAssembly implementation -> https://confluence.jnj.com/display/AFWN/FormAssembly+proxy
 *
 * Loads a FormAssembly form and embdeds it into the page.
 * FormAssembly REST urls are expected to be in this format:
 *      https://janssenpharmaceutica.tfaforms.net/rest/forms/view/25
 *
 * To be able to embed the content in our page avoiding CORS errors, we use a proxy:
 *      https://[PROXY_URL]/formassembly/rest/forms/view/25
 *
 * PROXY_URL will be our own domain.
 *
 * See:
 *   - Frontend IAC: https://sourcecode.jnj.com/projects/ASX-NBFS/repos/jmc_frontend_iac/browse
 *   - Packages:
 *      - packages/cf-tfa-origin-request-fn
 *      - packages/cf-tfa-origin-response-fn
 *
 * Scripts are added directly into the DOM controlling their loading state.
 * Other elements in the form header are added into our <head> using Helmet.
 * The form itself is parsed using html-react-parser and added into the body of our component.
 *
 */
export const FormAssemblyLegacy = ({
    formAssemblyRestUrl,
    proxyUrl = process.env.GATSBY_API_BASE_URL,
    formAssemblyDomain = "https://janssenpharmaceutica.tfaforms.net",
    compliance = false,
    complianceText = "",
    params,
    title,
}: Props): JSX.Element => {
    // For local testing you can proxy through the production url to avoid SSO.
    // https://*.janssenmedicalcloud.com has been included in csp whitelist for this reason
    // To get around CORS errors you can install the Moesif Origin & CORS Changer extension for chrome and configure the Origin, Access-Control-Allow-Origin. Also make sure to put "x-amz-server-side-encryption" in Access-Control-Allow-Headers and set Access-Control-Allow-Credentials to true.
    // https://chrome.google.com/webstore/detail/moesif-origin-cors-change/digfbfaphojjndkpccljibejjbppifbc
    // or if downloading extension is blocked locally you could also try disabling it on a local browser by
    // https://medium.com/@beligh.hamdi/run-chrome-browser-without-cors-872747142c61#:~:text=Paste%20chrome.exe%20%2D%2Duser,worrying%20about%20the%20CORS%20errors.
    // Use this to disable CORS when running the forms on localhost
    // Uncomment the line below and comment out the one below that
    // const gatsByApiBaseUrl = stripSlash.stripTrailing("https://www.janssenmedicalcloud.com/");
    const gatsByApiBaseUrl = stripSlash.stripTrailing(proxyUrl);

    const srcUrl = formAssemblyRestUrl?.match(/^(http|https):\/\//i)
        ? formAssemblyRestUrl?.replace(formAssemblyDomain, `${gatsByApiBaseUrl}/formassembly`)
        : "";

    const [formUrl, setFormUrl] = useState(new URL(srcUrl));
    const formId = formAssemblyRestUrl?.slice(formAssemblyRestUrl.lastIndexOf("/") + 1);
    const componentId = cleanCSSIdentifier(`formassembly-${formId}`);

    if (params) {
        Object.keys(params).forEach((key) => {
            formUrl.searchParams.append(key, params[key]);
        });
    }

    const [formHeader, setFormHeader] = useState(null);
    const [formBody, setFormBody] = useState(null);
    const [scriptStatus, setScriptStatus] = useState([]);
    const [errorMessage, setErrorMessage] = useState(null);
    const [displayReady, setDisplayReady] = useState(false);
    const [filesUploaded, setFilesUploaded] = useState(false);
    const nbrLink = useRef(0);
    const nbrLinkFinished = useRef(0);
    const { t } = useTranslation();

    // Get FormAssembly data and format it into header and form data
    const parseFormData = (formRawData: string): { formHeader: JSX.Element[]; formBody: JSX.Element[] } => {
        const FORM_BODY_DELIMITER = "<!-- FORM: BODY SECTION -->";
        // Workaround for bug where some forms don't have a body section defined with comments
        // https://jira.jnj.com/browse/AFWN-13985
        // Adding the delimiter when it's not already there
        const separatedFormData = formRawData.includes(FORM_BODY_DELIMITER)
            ? formRawData
            : formRawData.replace(
                  '<div class="wFormContainer"',
                  `${FORM_BODY_DELIMITER}
                   <div class="wFormContainer"`,
              );

        const formHeaderData =
            separatedFormData.includes("<head>") && separatedFormData.includes("</head>")
                ? separatedFormData.substring(
                      separatedFormData.indexOf("<head>") + 6,
                      separatedFormData.indexOf("</head>") - 1,
                  )
                : separatedFormData.includes(FORM_BODY_DELIMITER)
                ? separatedFormData?.split(FORM_BODY_DELIMITER)?.[0] || ""
                : "";

        const formBodyData =
            separatedFormData.includes("<body>") && separatedFormData.includes("</body>")
                ? separatedFormData.substring(
                      separatedFormData.indexOf("<body>") + 6,
                      separatedFormData.indexOf("</body>") - 1,
                  )
                : separatedFormData.includes(FORM_BODY_DELIMITER)
                ? separatedFormData?.split(FORM_BODY_DELIMITER)?.[1]
                : separatedFormData;

        const scriptsFromBody = Array.from(formBodyData.matchAll(/(<script[\s\S]*?script>)/gm), (m) => m[0]).toString();
        const cleanedBody = formBodyData.replace(/<script[\s\S]*?script>/gm, "");

        let formHeader = parse(formHeaderData, { trim: true }) || ([] as JSX.Element[]);
        let formBody = (parse(cleanedBody, { trim: true }) as JSX.Element[]) || ([] as JSX.Element[]);
        let additionalScripts = parse(scriptsFromBody, { trim: true }) || ([] as JSX.Element[]);

        if (typeof formHeader === "string" || !Array.isArray(formHeader))
            formHeader = [formHeader as unknown as JSX.Element];
        if (typeof formBody === "string" || !Array.isArray(formBody)) formBody = [formBody as unknown as JSX.Element];
        if (typeof additionalScripts === "string" || !Array.isArray(additionalScripts))
            additionalScripts = [additionalScripts as unknown as JSX.Element];
        additionalScripts = additionalScripts.filter((script) => script != ",");
        formHeader = formHeader.concat(additionalScripts);
        return { formHeader, formBody };
    };

    // Update the load status of external scripts in our state when the are loaded.
    // TODO: error control here will be nice
    const setStateFromEvent = (event: Event) => {
        const src = event?.target?.src;
        if (event.type === "load") {
            setScriptStatus((prevScriptStatus) => {
                const newStatus = prevScriptStatus.map((item) => {
                    if (src === item.script.src) {
                        return { script: item.script, src: src, status: SCRIPT_STATUS.LOADED };
                    }
                    return item;
                });

                return newStatus;
            });
        }
    };

    // Each script tag is added to our state to keep track of their loading status.
    const processScript = (elem: JSX.Element): void => {
        if (!compliance) {
            // No compliance, no scripts.
            return;
        }

        let script: HTMLScriptElement = document.querySelector(`script[src="${elem?.props["src"]}"]`);
        script = document.createElement("script");
        script.async = elem?.props?.async;
        script.defer = elem?.props?.defer;

        if (elem.props["src"]) {
            script.src = elem?.props["src"];
            if (elem?.props["data-for"]) {
                script.setAttribute("data-for", elem?.props["data-for"]);
            }

            // For external scripts, we add an event to wait for them to load
            script.addEventListener("load", setStateFromEvent);
        } else {
            script.text = elem?.props?.dangerouslySetInnerHTML?.__html;
        }

        setScriptStatus((prevScriptStatus) => [
            ...prevScriptStatus,
            {
                script: script,
                src: elem.props["src"],
                status: SCRIPT_STATUS.NOT_LOADED,
            },
        ]);
    };

    // Handle our status for external script to see when they can be loaded.
    // We need wait for the external scripts be be fully loaded to continue.
    useEffect(() => {
        scriptStatus.every((item) => {
            if (item.status === SCRIPT_STATUS.NOT_LOADED) {
                // Add the script into the DOM
                // Script tags rendered by React won't get executed.
                // Helmet will, but then we won't be able to control if they are fully loaded.
                document.head.appendChild(item.script);

                // For inline scripts we don't need wait for a load event,
                // so we set them as loaded already.
                if (!item.src) {
                    setScriptStatus((prevScriptStatus) => {
                        prevScriptStatus[prevScriptStatus?.indexOf(item)] = {
                            script: item.script,
                            src: item.src,
                            status: SCRIPT_STATUS.LOADED,
                        };
                        return [...prevScriptStatus];
                    });
                }
                return false;
            }
            return true;
        });

        // When all scripts are loaded call the DOMContentLoaded event
        if (scriptStatus.every((item) => item.status === SCRIPT_STATUS.LOADED)) {
            window.document.dispatchEvent(
                new Event("DOMContentLoaded", {
                    bubbles: true,
                    cancelable: true,
                }),
            );
        }
    }, [scriptStatus]);

    //this function will determine if the number of links who have finished loading is equal to the total number of link generated by the form. If the tw are equal then it allows the form to display, preventing a form to display before the css has been downloaded
    function increaseNbrLinkFinished(): void {
        nbrLinkFinished.current = nbrLinkFinished.current + 1;
        if (nbrLinkFinished.current === nbrLink.current) {
            setDisplayReady(true);
        }
    }

    useEffect(() => {
        // Load the FormAssembly form, parse it and process it.
        axios.get(formUrl.toString()).then((response) => {
            if (response && response?.status === 200) {
                const { formHeader, formBody } = parseFormData(response?.data);

                // Captcha scripts must be moved to the last position to avoid errors.
                const headerItems = [] as JSX.Element[];
                const appendItems = [] as JSX.Element[];

                formHeader?.forEach((item: JSX.Element) => {
                    if (!(item.type === "script" && item.props["src"]?.includes("recaptcha"))) {
                        headerItems.push(item);
                    } else {
                        appendItems.push(item);
                    }
                });
                const orderedHeader = headerItems.concat(appendItems);

                // Cleanup and format it for Helmet
                const parsedHeader = orderedHeader?.map((elem: JSX.Element, index: number) => {
                    let line = null;
                    if (elem?.type == "style") {
                        line = <style key={`style-${index}`}>{elem.props.dangerouslySetInnerHTML.__html}</style>;
                    } else if (elem?.type === "script") {
                        line = processScript(elem);
                    } else if (elem?.type === "link" && elem?.props?.rel.includes("stylesheet")) {
                        //in case of stylesheet, increase the number of links by one and had a function to be called when the link is done loading
                        nbrLink.current = nbrLink.current + 1;
                        line = cloneElement(elem, {
                            onLoad: increaseNbrLinkFinished,
                        });
                    } else if (elem?.type !== "meta") {
                        line = elem;
                    }

                    return line;
                });

                // There can be script tags in the body, we must process them as any other script in the header.
                const parsedForm = formBody?.map((elem: JSX.Element) => {
                    // Recursive function to traverse the JSX tree
                    const processElements = (element: JSX.Element): Promise<JSX.Element> => {
                        if (element?.type === "script") {
                            processScript(element);
                        }

                        let children = null;
                        if (Array.isArray(element?.props?.children)) {
                            children = element.props.children.map((elem: JSX.Element) => {
                                return processElements(elem);
                            });
                        } else if (element?.props?.children) {
                            children = processElements(element?.props?.children);
                        }
                        const value = element?.props?.value || element?.props?.defaultValue;

                        return children
                            ? React.cloneElement(element, { ...(value || value === "" ? { value } : null), children })
                            : element;
                    };

                    return processElements(elem);
                });
                if (nbrLink.current === 0 && nbrLinkFinished.current === 0) {
                    setDisplayReady(true);
                }

                setFormHeader(parsedHeader);
                setFormBody(parsedForm);
            } else {
                setErrorMessage(t("An unknown error occurred", { ns: "errors" }));
            }
        });
    }, [formUrl]);

    const resetSubmitButton = (button: HTMLButtonElement) => {
        button.disabled = false;
        button.value = "Submit";
        button.style.color = "";
    };

    const getS3PresignedUrls = async (fileInputs: HTMLInputElement[]): Promise<any[]> => {
        try {
            const fileBody = fileInputs.reduce(
                (acc, cur) =>
                    cur.files.length > 0 ? [...acc, { name: cur.files[0].name, size: cur.files[0].size }] : acc,
                [],
            );

            if (fileBody.length <= 0) return [];

            const { data } = await axios.post(`${urlHelper.getApiBaseUrl()}files/upload/presigned`, {
                files: fileBody,
            });
            return data;
        } catch (e) {
            setErrorMessage(`Something went wrong while generating the file upload urls. Try submitting again`);
        }
    };

    const uploadFiles = async (fileInputs: HTMLInputElement[], presignedData: any[]) => {
        // filemapping in case of multiple files with same name (perhaps even same files in different fields)
        const fileMapping: any = {};
        presignedData.forEach((presigned) => {
            const file = fileInputs.find(
                (file) =>
                    file.files?.[0]?.name === presigned.name &&
                    file?.files?.[0]?.size === presigned.size &&
                    !(file.id in fileMapping),
            );
            (fileMapping as any)[presigned.uniqueIdentifier] = file?.id;
        });

        try {
            await Promise.all(
                presignedData.map(async (presigned) => {
                    await fileUpload(
                        presigned,
                        fileInputs.find((file) => file?.id === fileMapping[presigned.uniqueIdentifier]),
                    );
                }),
            );
            setFilesUploaded(true);
            return fileMapping;
        } catch (e) {
            setErrorMessage(`Something went wrong while uploading the files. Try submitting again`);
        }
    };

    const fileUpload = async (presigned: any, file: HTMLInputElement) => {
        let tries = 0;
        let successfulUpload = false;
        while (!successfulUpload) {
            try {
                return await axios.put(presigned.presignedUrl, file.files[0], {
                    headers: {
                        ...presigned.neededHeaders,
                        "Content-Type": file.files[0].type,
                    },
                });
            } catch (e) {
                tries++;
                if (tries >= MAX_TRIES_FILE_UPLOAD) {
                    setErrorMessage(
                        `Something went wrong while uploading the files, exceeded maximum number of tries: ${MAX_TRIES_FILE_UPLOAD}. Refresh and try again`,
                    );
                    throw e;
                }
            }
        }
        return;
    };

    const formUpload = async (body: any, submitButton: HTMLButtonElement) => {
        try {
            const { data } = await axios.post(`${urlHelper.getApiBaseUrl()}formassembly/upload`, body);

            return data?.redirect;
        } catch (e) {
            setErrorMessage(`Something went wrong while responding to the form. Refresh and try again`);
            resetSubmitButton(submitButton);
            throw e;
        }
    };

    // Using this to recognise if formassembly input is part of a tooltip
    // when a tooltip is configure as a popup it creates a button input with an "X" to close it
    // if formassembly changes structure of form this will possibly break
    const isNotTooltipInput = (element: HTMLInputElement) => {
        return !Array.from(element.parentElement.classList).some((specificClass) =>
            specificClass.includes("field-hint"),
        );
    };

    const getOptionsFromMultipleSelect = (selectElement: HTMLSelectElement) =>
        Array.from(selectElement.options).reduce((acc, cur) => (cur.selected ? [...acc, cur.value] : acc), []);

    const onFormSubmit = (form: HTMLFormElement) => async (event: Event) => {
        event.preventDefault();

        const valid = await new Promise((resolve) =>
            setTimeout(() => {
                const errorFields = event?.target?.querySelectorAll(".errFld");
                if (errorFields?.length) {
                    errorFields?.forEach((field) => {
                        const fieldName = field?.querySelector("label")?.textContent;
                        const errMessage = field?.querySelector(".errMsg")?.textContent;
                        analyticstracker().trackEvent({
                            event: EventTypes.TOOL_ERROR,
                            info: {
                                name: title,
                                type: "FormAssembly",
                                stepnumber: "0",
                                stepname: "form",
                                fieldname: fieldName,
                                errormessage: errMessage,
                                errortype: "user",
                            },
                        });
                        resolve(false);
                    });
                } else resolve(true);
            }, 500),
        );

        if (!valid) return false;

        const submitButton = Array.from(form.elements).filter((element) => element.id.startsWith("submit_button"))[0];
        const nonFileElements = Array.from(form.elements).filter(
            (element) =>
                element.id.startsWith("tfa_") &&
                (element as HTMLInputElement).type !== "file" &&
                (element as HTMLInputElement).type !== "radio" &&
                (element as HTMLInputElement).type !== "checkbox" &&
                isNotTooltipInput(element as HTMLInputElement),
        );
        const booleanElements = Array.from(form.elements).filter(
            (element) =>
                element.id.startsWith("tfa_") &&
                ((element as HTMLInputElement).type === "radio" || (element as HTMLInputElement).type === "checkbox") &&
                isNotTooltipInput(element as HTMLInputElement),
        );
        const fileElements = Array.from(form.elements).filter(
            (element) =>
                element.id.startsWith("tfa_") &&
                (element as HTMLInputElement).type === "file" &&
                isNotTooltipInput(element as HTMLInputElement),
        );

        const postBody: any = {};
        for (let element of nonFileElements as HTMLInputElement[]) {
            if (element.tagName == "SELECT") {
                postBody[element.name] = element.multiple
                    ? getOptionsFromMultipleSelect(element as any as HTMLSelectElement)
                    : element.value;
            } else {
                postBody[element.name] = element.value;
            }
        }
        for (let element of booleanElements as HTMLInputElement[]) {
            if ((element as HTMLInputElement).checked) postBody[element.name] = (element as HTMLInputElement).value;
        }
        for (let element of booleanElements) {
            if ((element as HTMLInputElement).checked) postBody[element.id] = (element as HTMLInputElement).value;
        }

        const captchaResponse = Array.from(form.elements).find((element) =>
            element.id.startsWith("g-recaptcha-response"),
        ) as HTMLTextAreaElement;

        if (captchaResponse?.value) {
            postBody["g-recaptcha-response"] = captchaResponse.value;
        }

        let files = undefined;
        if (!filesUploaded && fileElements.length > 0) {
            // filesize check -> max 35MB
            const totalFileSize = (fileElements as HTMLInputElement[]).reduce(
                (acc, cur) => acc + (cur?.files?.[0]?.size || 0),
                0,
            );
            if (totalFileSize > MAX_AGGREGATE_UPLOAD_SIZE) {
                setErrorMessage("Exceeded maximum aggregate file size");
                resetSubmitButton(submitButton as HTMLButtonElement);
                return false;
            }
            const presignedUrls = await getS3PresignedUrls(fileElements as HTMLInputElement[]);

            const fileMapping = await uploadFiles(fileElements as HTMLInputElement[], presignedUrls);

            files = presignedUrls.map((presigned) => ({
                name: presigned.name,
                uniqueIdentifier: presigned.uniqueIdentifier,
                key: (fileMapping as any)[presigned.uniqueIdentifier],
            }));
        }

        const body = {
            postBody,
            isMultipart: fileElements.length > 0,
            ...(files && files.length > 0 && { files }),
        };

        const redirect = await formUpload(body, submitButton as HTMLButtonElement);

        if (redirect && !redirect.includes("forms")) {
            analyticstracker().trackEvent({
                event: EventTypes.TOOL_LASTSTEP,
                info: {
                    name: title,
                    type: "FormAssembly",
                    stepnumber: "1",
                    stepname: "form_laststep",
                },
            });
            window.location = redirect;
        } else if (redirect && redirect.includes("forms")) {
            setFormUrl(new URL(redirect));
        } else {
            analyticstracker().trackEvent({
                event: EventTypes.TOOL_ERROR,
                info: {
                    name: title,
                    type: "FormAssembly",
                    stepnumber: "0",
                    stepname: "form",
                    errormessage: "Something went wrong while responding to the form",
                    errortype: "user",
                },
            });
            resetSubmitButton(submitButton as HTMLButtonElement);
        }

        return false;
    };

    useEffect(() => {
        const formContainer = document?.getElementById(componentId);
        const form = formContainer?.getElementsByTagName("form")[0];
        const mySubmit = onFormSubmit(form);
        form?.addEventListener("submit", mySubmit);
        const resetFileUploadStatus = () => setFilesUploaded(false);
        const fileInputs = Array.from(form?.elements || []).filter(
            (element) => element.id.startsWith("tfa_") && (element as HTMLInputElement).type === "file",
        );
        fileInputs.forEach((fileInput) => {
            fileInput.addEventListener("change", resetFileUploadStatus);
        });

        return (): void => {
            form?.removeEventListener("submit", mySubmit);
            fileInputs.forEach((fileInput) => {
                fileInput.removeEventListener("change", resetFileUploadStatus);
            });
        };
    });

    useEffect(() => {
        if (window.location.search.match(/internal=(true|1)/i) && displayReady) {
            const webidRegex = /^[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i;
            document.querySelectorAll("input:not([value=''])").forEach((item) => {
                if (item.value.toString().match(webidRegex)) {
                    item.value = item.value + "_internal";
                }
            });
            const emailRegex =
                /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
            document.querySelectorAll("input:not([value=''])").forEach((item) => {
                if (item.value.toString().match(emailRegex)) {
                    item.value = "PLEASE FILL OUT YOUR TESTING EMAIL ADDRESS HERE";
                }
            });
            alert(
                "We detected you are testing this form submission internally. Please make sure to use a fake email address!\nWe don't want to trigger emails to real people.",
            );
        }
    }, [displayReady]);

    const component = useMemo(() => {
        return (
            <div id={componentId} key={componentId} className={style["formassembly-webform"]}>
                {formHeader}
                {compliance ? (
                    <>
                        {formBody && displayReady ? (
                            <>
                                <ToolTracking
                                    eventType={EventTypes.TOOL_STEP}
                                    name={title}
                                    type="FormAssembly"
                                    stepnumber="0"
                                    stepname="form"
                                />
                                <div key="form-data">{formBody}</div>
                            </>
                        ) : (
                            <div className={style.loading}>
                                <Spinner size="large" />
                            </div>
                        )}
                        {errorMessage && (
                            <Typography key="error" color="error">
                                {errorMessage}
                            </Typography>
                        )}
                    </>
                ) : (
                    <Typography data-test-id="Compliance-Error-Message">{complianceText}</Typography>
                )}
            </div>
        );
    }, [formBody, formHeader, displayReady, errorMessage]);

    return component;
};
